import { useEffect, useState } from "react";
import { PagedResults } from "../../types/PagedResults";
import { Advert } from "../../types/Adverts";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAdvertsService from "../../services/AdvertsService";
import PageTitle from "../../components/PageTitle";
import PositiveButton from "../../components/PositiveButton";
import AddIcon from "@mui/icons-material/Add";
import Ghost from "../../components/Ghost";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";

export default function AdminAdvertsListPage() {

    const [isSearching, setIsSearching] = useState(false);
    const [results, setResults] = useState<PagedResults<Advert> | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const navigate = useNavigate();

    const [formData, setFormData] = useState({
      searchValue: searchParams.get("searchValue") || "",
      page: parseInt(searchParams.get("page") || "1")
    });

    const {searchAdverts} = useAdvertsService();
    const debounceDelay = 500;

    const search = () => {
    setIsSearching(true);

    setSearchParams({
      searchValue: formData.searchValue,
      page: formData.page.toString(),
    });

    searchAdverts(formData.searchValue, formData.page, 10)
      .then((searchResults) => {
        setResults(searchResults);
      })
      .catch((error) => {
        console.error("Error searching adverts:", error);
      })
      .finally(() => {
        setIsSearching(false);
      });
    };

    const handleInputChange = (e: any) => {
      const { name, value } = e.target;

      setFormData({
        ...formData,
        [name]: value,
        page: 1, // Reset to the first page when searching
      });
    };

    const handlePageChange = (newPage: number) => {
      setFormData((prev) => ({
        ...prev,
        page: newPage,
      }));
    };

    useEffect(() => {
      const handler = setTimeout(() => {
        search();
      }, debounceDelay);

      return () => {
        clearTimeout(handler);
      };
    }, [formData.searchValue, formData.page]);

    useEffect(() => {
      search();
    }, []);

  return (
    <div className="flex flex-col gap-4">
      <section role="heading" className="flex flex-row">
        <PageTitle className="mr-4">Adverts</PageTitle>
        <PositiveButton onClick={() => navigate("/admin/adverts/new")}>
          <AddIcon /> Add new advert
        </PositiveButton>
        <div
          role="searchbox"
          className="flex flex-row gap-2  px-4 items-center xl:col-span-2 flex-grow"
        >
          <label
            htmlFor="search"
            className="hidden xl:w-auto xl:block leading-6"
          >
            Filter:
          </label>
          <div className="mt-2 w-full">
            <input
              type="text"
              name="searchValue"
              placeholder="Search term"
              value={formData.searchValue}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded text-black"
            />
          </div>
        </div>
      </section>
      <section role="content">
        <table className="table-auto w-full card border-spacing-2">
          <thead>
            <tr className="">
              <th className="font-normal text-left p-4">Image</th>
              <th className="font-normal text-left p-4">Name</th>
              <th className="font-normal text-left p-4">Title</th>
              <th className="font-normal text-left p-4">Start time</th>
              <th className="font-normal text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {isSearching &&
              Array.from({ length: 3 }, (_, i) => (
                <tr className="border-t border-cyan-900" key={i}>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                  <td className="text-left p-4">
                    <Ghost additionalClasses="p-4 rounded" />
                  </td>
                </tr>
              ))}

            {!isSearching && results && (
              <>
                {!results.items || results.items.length === 0 ? (
                  <tr className="border-t border-cyan-900">
                    <td className="text-left p-4" colSpan={5}>
                      No results found.
                    </td>
                  </tr>
                ) : (
                  results.items.map((advert) => (
                    <tr
                      className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer"
                      key={advert.id}
                      onClick={() => navigate(`/admin/adverts/${advert.id}/`)}
                      title={`View advert ${advert.title}`}
                    >
                      <td className="text-left p-4"><img src={advert.imageUrl} alt={advert.title} className="w-32 h-18 object-cover rounded" /></td>
                      <td className="text-left p-4">{advert.name}</td>
                      <td className="text-left p-4">{advert.title}</td>
                      <td className="text-left p-4">{advert.startTime.toString()}</td>
                      <td className="p-4 w-auto text-right">
                        <ArrowForwardTwoToneIcon />
                      </td>
                    </tr>
                  ))
                )}
              </>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
